/**
 * Saving and loading a basket — Basket brief #4. Pure: explicit inputs, no DOM,
 * no clock, no network. live.html and the API move the bytes; the rules live
 * here so they are testable.
 *
 * ANALYSIS ONLY — loading a basket builds legs for the analyser. Nothing here
 * can reach /api/live/order; execution is brief #5.
 *
 * ── A saved basket is a TEMPLATE, not a record (D2) ───────────────────────
 * Only the bound identity from brief #1 is stored — underlying, exchange,
 * expiry, strike, optType, side, lots, symbol. Entry prices are RE-CAPTURED
 * from live LTP on load, exactly as adding a leg does, so a loaded basket's
 * P&L starts at zero against today's prices. That must be said on screen: a
 * user who thinks they are looking at last week's position would read every
 * number wrong.
 *
 * ── Two things are never stored (D4) ──────────────────────────────────────
 * The broker `tsym`, because it is resolved per session against SearchScrip and
 * can change — a stale one loaded months later is a wrong-contract risk on the
 * money path once #5 exists. And live LTPs / margin, which are stale the moment
 * they are written and would render as if current.
 *
 * ── Expiry staleness is the sharp edge (D3) ───────────────────────────────
 * A saved basket's expiry WILL eventually be in the past, and those contracts
 * no longer exist. Such legs load as UNLOADABLE and say why. Nothing is ever
 * silently re-pointed at the nearest live expiry: that plausible-looking
 * substitution is the class of bug behind the SENSEX weekly incident (24ae990),
 * where the substitution hid the fact that the real contract was not there. A
 * basket that half-loads with two legs quietly moved to another expiry is worse
 * than one that refuses. `rollExpiry` exists for when the user asks for it —
 * explicitly, with the new date visible.
 */

import { makeBasketLeg, type BasketLeg, type OptType, type Side } from "./leg";
import { marketExchange } from "../symbols";

/** Exactly the fields that persist. Anything else is dropped before saving. */
export interface SavedLeg {
  underlying: string;
  exchange: string;
  expiry: string;
  strike: number;
  optType: OptType;
  side: Side;
  lots: number;
  symbol: string;
}

export const SAVED_LEG_FIELDS: ReadonlyArray<keyof SavedLeg> = [
  "underlying", "exchange", "expiry", "strike", "optType", "side", "lots", "symbol",
];

export const MAX_NAME_LEN = 60;

export type UnloadableReason = "expired" | "malformed" | "no-lot-size";

export interface UnloadableLeg {
  leg: SavedLeg;
  reason: UnloadableReason;
  /** Human sentence for the panel — the "say why" half of D3. */
  detail: string;
}

export interface LoadedBasket {
  legs: BasketLeg[];
  unloadable: UnloadableLeg[];
  /** Distinct expiries that are in the past — what a "roll" would have to move. */
  expiredExpiries: string[];
}

// ── saving ──────────────────────────────────────────────────────────────────

/**
 * Reduce live legs to what is persisted (D4).
 *
 * Note what is NOT copied: `tsym` and `entry`. This is a whitelist rather than
 * a delete-list on purpose — a field added to BasketLeg later is excluded by
 * default, so the next person to add something sensitive does not have to
 * remember this file.
 */
export function toSavedLegs(legs: BasketLeg[]): SavedLeg[] {
  return (legs || []).filter(Boolean).map(l => ({
    underlying: l.underlying,
    exchange: l.exchange,
    expiry: l.expiry,
    strike: Number(l.strike),
    optType: l.optType,
    side: l.side,
    lots: Math.max(1, Number(l.lots) || 1),
    symbol: l.symbol,
  }));
}

// ── loading ─────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` is expired once `today` (same format) is strictly past it. */
export function isExpired(expiry: string, today: string): boolean {
  if (!expiry || !today) return false;         // unknown: never call it expired
  return String(expiry) < String(today);
}

function wellFormed(leg: unknown): leg is SavedLeg {
  const l = leg as Partial<SavedLeg> | null;
  return !!l
    && typeof l.underlying === "string" && !!l.underlying
    && typeof l.exchange === "string" && !!l.exchange
    && typeof l.expiry === "string" && !!l.expiry
    && typeof l.symbol === "string" && !!l.symbol
    && (l.optType === "CE" || l.optType === "PE")
    && (l.side === "BUY" || l.side === "SELL")
    && isFinite(Number(l.strike));
}

/**
 * Rebuild live legs from a saved basket.
 *
 * `opts.today` is passed in rather than read from a clock, so the expiry rule
 * is testable and so a single load cannot straddle midnight. `opts.entryFor`
 * re-captures the entry price per leg (D2, template); returning null leaves the
 * leg with no basis, which brief #2 already handles by excluding it from the
 * total and saying so.
 *
 * `opts.lotSizeFor` supplies the contract multiplier, which is NOT persisted —
 * it belongs to the contract, not to the saved shape, so it is resolved fresh
 * (NIFTY 65, SENSEX 20) rather than trusted from an old record.
 */
export function fromSavedLegs(
  saved: unknown[],
  opts: {
    today: string;
    idFor: (index: number) => string;
    entryFor?: (leg: SavedLeg) => number | null | undefined;
    lotSizeFor?: (leg: SavedLeg) => number;
  },
): LoadedBasket {
  const legs: BasketLeg[] = [];
  const unloadable: UnloadableLeg[] = [];
  const expired = new Set<string>();

  (saved || []).forEach((raw, i) => {
    if (!wellFormed(raw)) {
      unloadable.push({
        leg: raw as SavedLeg,
        reason: "malformed",
        detail: "Leg is missing part of its contract identity and cannot be rebuilt.",
      });
      return;
    }
    const leg = raw as SavedLeg;
    if (isExpired(leg.expiry, opts.today)) {
      // D3: refuse, name the expiry, and change NOTHING about it.
      expired.add(leg.expiry);
      unloadable.push({
        leg,
        reason: "expired",
        detail: `Expiry ${leg.expiry} has passed — this contract no longer exists. `
          + "Not loaded, and NOT substituted with a live expiry.",
      });
      return;
    }
    // A leg with no CONTRACT MULTIPLIER cannot be loaded. This looks like a
    // nicety and is not: makeBasketLeg coerces a missing lotSize to 1
    // (leg.ts:114), so loading anyway would silently build a leg whose quantity
    // is 1 instead of 65 (NIFTY) or 20 (SENSEX) — every downstream number
    // (P&L, margin, payoff, and the order qty once brief #5 is enabled) wrong by
    // that factor, with nothing on screen to say so. Brief #1's rule applies:
    // refuse rather than guess, because a wrong lot size is a quantity error.
    const lotSize = opts.lotSizeFor ? Number(opts.lotSizeFor(leg)) : 0;
    if (!(lotSize > 0)) {
      unloadable.push({
        leg,
        reason: "no-lot-size",
        detail: `No lot size available for ${leg.underlying} ${leg.expiry} — switch the toolbar to `
          + "that contract and load again. Not loaded, because guessing the multiplier would make "
          + "every quantity and P&L figure wrong without saying so.",
      });
      return;
    }
    const entry = opts.entryFor ? opts.entryFor(leg) : null;
    legs.push(makeBasketLeg(
      {
        underlying: leg.underlying,
        // ORDER-1 MIGRATION. Baskets saved before that fix stored live.html's
        // BROKER SEGMENT ("BFO") in a field documented as a MARKET exchange
        // ("BSE"). Normalising here — at the point the restored leg is built —
        // means a basket saved before the fix loads correct rather than
        // reloading the defect that could not place a SENSEX order. A value
        // already in the market vocabulary passes through unchanged.
        exchange: marketExchange(leg.exchange),
        expiry: leg.expiry,
        lotSize,
      },
      {
        id: opts.idFor(i),
        strike: leg.strike,
        optType: leg.optType,
        side: leg.side,
        lots: leg.lots,
        entry: entry == null ? null : Number(entry),
        symbol: leg.symbol,
        // tsym deliberately omitted — re-resolved per session, never restored.
      },
    ));
  });

  return { legs, unloadable, expiredExpiries: [...expired].sort() };
}

/**
 * Move saved legs to a new expiry — the EXPLICIT "roll" action.
 *
 * Never called automatically. The symbol is dropped so it is rebuilt inside the
 * new expiry by makeBasketLeg; carrying the old one across would keep pointing
 * at the dead contract while the date on screen said otherwise.
 */
export function rollExpiry(saved: SavedLeg[], newExpiry: string): SavedLeg[] {
  return (saved || []).map(l => ({ ...l, expiry: newExpiry, symbol: "" }));
}

// ── names (D5) ──────────────────────────────────────────────────────────────

export interface NameCheck { ok: boolean; name: string; error?: string }

/**
 * Validate a basket name. Length-bounded and non-blank; the CONTENT is left
 * alone deliberately — the defence against a name containing markup is that the
 * UI writes it with textContent, never into an innerHTML string (blueprint R19,
 * and T016/T017 are open: do not add a new unescaped sink). Sanitising here
 * instead would silently mangle legitimate names and still leave the sink.
 */
export function validateBasketName(name: unknown): NameCheck {
  const text = (name == null ? "" : String(name)).trim();
  if (!text) return { ok: false, name: text, error: "A basket name is required" };
  if (text.length > MAX_NAME_LEN) {
    return { ok: false, name: text, error: `Name too long (max ${MAX_NAME_LEN} characters)` };
  }
  return { ok: true, name: text };
}
